import React, { useState } from 'react';
import { useProject } from '../../hooks/useProject';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';
import { useProjectIdFromRoute } from '../../hooks/useProjectIdFromRoute';
import { NOT_FOUND_META, useNotFoundRedirect } from '../../hooks/useNotFoundRedirect';
import { useInitialSearch } from '../../hooks/useInitialSearch';
import { getRouteParams } from '../../services/angular-bridge';
import { Tabs } from '../../components/Tabs';
import { PageHeader } from '../../components/PageHeader';
import { Breadcrumb } from '../../components/Breadcrumb';
import { hasPermission } from '../../types/project';
import { NewCollectionModal } from './NewCollectionModal';
import { HomeTab } from './HomeTab';
import { PreProcessingTab } from './PreProcessingTab';
import { PostProcessingTab } from './PostProcessingTab';
import { CollectionsTab } from './CollectionsTab';
import { CustodiansTab } from './CustodiansTab';
import { TagsTab } from './TagsTab';
import { ReportsTab } from './ReportsTab';
import { ExportsTab } from './ExportsTab';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';

export function ProjectDetailPage() {
  const projectId = useProjectIdFromRoute();
  const projectQuery = useProject(projectId, NOT_FOUND_META);
  const { data: project, isLoading } = projectQuery;
  const [activeTab, setActiveTab] = useState('home');
  const [collectionsFilter, setCollectionsFilter] = useState('');
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);

  useProjectPageMount(project, 'projects', 'home');
  useNotFoundRedirect(projectQuery);
  // After the mount hook has made this the current project. `?q=` from SSO deep links.
  const { q } = getRouteParams();
  useInitialSearch(typeof q === 'string' ? q : undefined, 1, !!project);

  function handleCustodianClick(name: string) {
    setCollectionsFilter(name);
    setActiveTab('collections');
  }

  const tabs = project ? [
    { id: 'home', heading: 'Home', content: <HomeTab projectId={projectId} /> },
    ...(hasPermission(project, 'VIEW_PRE_PROCESSING_TAB') ? [{ id: 'pre-processing', heading: 'Pre-Processing Overview', content: <PreProcessingTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_POST_PROCESSING_TAB') ? [{ id: 'post-processing', heading: 'Post-Processing Overview', content: <PostProcessingTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_COLLECTIONS_TAB') ? [{ id: 'collections', heading: 'Collections', content: <CollectionsTab projectId={projectId} externalFilter={collectionsFilter} /> }] : []),
    ...(hasPermission(project, 'VIEW_CUSTODIANS_TAB') ? [{ id: 'custodians', heading: 'Custodians', content: <CustodiansTab projectId={projectId} onCustodianClick={handleCustodianClick} /> }] : []),
    ...(hasPermission(project, 'VIEW_TAGS_TAB') ? [{ id: 'tags', heading: 'Tags', content: <TagsTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_REPORTS_TAB') ? [{ id: 'reports', heading: 'Reports', content: <ReportsTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_EXPORTS_TAB') ? [{ id: 'exports', heading: 'Exports', content: <ExportsTab projectId={projectId} /> }] : []),
  ] : [];

  return (
    <>
      {isLoading && <LoadingPlaceholder label="Loading project…" />}
      {project && (
        <>
          <PageHeader
            project={project}
            title={project.name}
            testId="project-home-header-panel"
            rightContent={hasPermission(project, 'MANAGE_COLLECTIONS') ? (
              <>
                <a
                  href="https://folio-accessid.zendesk.com/hc/en-us/articles/213127166-Adding-Data-To-Your-Project"
                  target="_blank"
                  rel="noreferrer"
                  title="Get Help"
                  data-testid="project-home-help-link"
                >
                  <i className="far fa-question-circle fa-lg" aria-hidden="true" />
                </a>
                {' '}
                <button
                  className="btn btn-primary"
                  onClick={() => setAddCollectionOpen(true)}
                  data-testid="project-home-add-data-btn"
                >
                  Add more data to this project <i className="fas fa-plus-circle" style={{ color: '#fff' }} aria-hidden="true" />
                </button>
              </>
            ) : undefined}
          />
          <Breadcrumb items={[
            { label: 'Projects', href: '/project' },
            { label: project.name },
          ]} testId="project-home-breadcrumb-list" />

          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} testId="project-home-tabs" />

          {addCollectionOpen && project && (
            <NewCollectionModal project={project} onClose={() => setAddCollectionOpen(false)} />
          )}
        </>
      )}
    </>
  );
}
